/**
 * Routing helpers for sender-renewal flows.
 *
 * - Payment completion ("Go to dashboard"): always Sender request dashboard — handled only in Payment / PaymentResult.
 * - Non-payment exits from RenewRegisterSender (cancel, pay later, errors): follow renewal entry (`renewalSource` query).
 */

import { RawLocation } from 'vue-router';

export const RENEWAL_PAYMENT_REDIRECT_STORAGE_KEY = 'renewalPaymentRedirectSuccess';

export type RenewalEntrySource = 'senderDashboard' | 'contractDashboard' | 'extendDuration';

const ENTRY_KEYS = ['senderDashboard', 'contractDashboard', 'extendDuration'] as const;

export function isRenewalEntrySource(val: unknown): val is RenewalEntrySource {
    return typeof val === 'string' && (ENTRY_KEYS as readonly string[]).includes(val);
}

/** Navigate targets matching ExtendDurationRequests dashboard ↔ Dashboard ↔ ConnectingRequestDashboard entry paths */
export function getRouteFromRenewalSource(queryRenewalSource: unknown): RawLocation {
    switch (queryRenewalSource) {
        case 'extendDuration':
            return { name: 'ExtendDurationRequests' };
        case 'contractDashboard':
            return { name: 'ConnectingRequestDashboard' };
        case 'senderDashboard':
            return { name: 'Dashboard' };
        default:
            return { name: 'Dashboard' };
    }
}

export function clearRenewalPaymentRedirectMark(): void {
    sessionStorage.removeItem(RENEWAL_PAYMENT_REDIRECT_STORAGE_KEY);
}

/** Route path has no `:ids` segment; params are dropped from the URL — use `query.ids` for stable reload/back navigation. */
export function parseSenderRenewalRouteIds(route: { params: { ids?: string }; query: Record<string, string | (string | null)[] | null | undefined> }): string | null {
    const raw = route.params.ids != null ? route.params.ids : route.query.ids;
    if (raw == null || raw === '') {
        return null;
    }
    if (Array.isArray(raw)) {
        return raw.length ? String(raw[0]) : null;
    }
    const s = String(raw).trim();
    return s.length ? s : null;
}

/**
 * IDs to reload RenewRegisterSender sender grid. After Pay-now, `query.ids` mirrors Payment/extend-request identifiers;
 * sender GUIDs must be supplied separately (`renewalSenderIds`) for get-senders-for-renew-by-senderIds.
 */
export function parseRenewalSenderIdsForGrid(route: { params: { ids?: string }; query: Record<string, string | (string | null)[] | null | undefined> }): string | null {
    const q = route.query.renewalSenderIds;
    if (q != null && q !== '') {
        const first = Array.isArray(q) ? (q.length ? String(q[0]) : '') : String(q);
        const t = first.trim();
        if (t.length) {
            return t;
        }
    }
    return parseSenderRenewalRouteIds(route);
}

const RENEWAL_DASHBOARD_SUCCESS_TOAST_KEY = 'renewalDashboardSuccessToastPending';

export function setRenewalDashboardSuccessToastPending(): void {
    sessionStorage.setItem(RENEWAL_DASHBOARD_SUCCESS_TOAST_KEY, '1');
}

export function clearRenewalToast(): void {
    sessionStorage.removeItem(RENEWAL_DASHBOARD_SUCCESS_TOAST_KEY);
}

export function takeRenewalDashboardSuccessToastPending(): boolean {
    const v = sessionStorage.getItem(RENEWAL_DASHBOARD_SUCCESS_TOAST_KEY);
    sessionStorage.removeItem(RENEWAL_DASHBOARD_SUCCESS_TOAST_KEY);
    return v === '1';
}
